import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/lib/session';
import { signOut } from '@/lib/auth';
import type { Tier } from '@/lib/access';
import { usePlansCatalog, discountPct, inr } from '@/lib/plansCatalog';
import { payForPlan, razorpayConfigured } from '@/lib/razorpay';

/** Full-screen block shown when the trial/plan has expired. The user can sign
    in but cannot use the app until they upgrade. */
export default function Paywall() {
  const navigate = useNavigate();
  const { restaurant, refreshAccess } = useSession();
  const { plans } = usePlansCatalog();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const configured = razorpayConfigured();

  const upgrade = async (tier: string, amount: number) => {
    setError(null);
    if (!configured) return;
    setBusy(tier);
    try {
      await payForPlan({
        plan: tier as Tier,
        amount,
        restaurantId: restaurant.id,
        onSuccess: async () => { await refreshAccess(); },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Payment could not be started.');
    } finally {
      setBusy(null);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background-50 px-6 py-10 overflow-y-auto">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2.5">
            <img src="/images/logo.png" alt="EzyDinePro" className="h-9 w-auto" />
          </div>
          <button
            onClick={handleSignOut}
            className="text-sm text-foreground-500 hover:text-foreground-700 cursor-pointer"
          >
            Sign out
          </button>
        </div>

        <div className="text-center max-w-xl mx-auto mb-8">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-50 flex items-center justify-center">
            <i className="ri-lock-2-line text-2xl text-accent-500"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-800">Your free trial has ended</h1>
          <p className="text-sm text-foreground-500 mt-2">
            Choose a plan to keep using EzyDine for <span className="font-medium text-foreground-700">{restaurant.name}</span>.
            Your data is safe — pick a plan below to unlock the dashboard again.
          </p>
        </div>

        {!configured && (
          <div className="max-w-xl mx-auto mb-6 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-center">
            Online payment isn't set up yet. Please contact support to activate a plan.
          </div>
        )}
        {error && (
          <div className="max-w-xl mx-auto mb-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-center">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <div
              key={plan.tier}
              className={`rounded-xl border bg-background-50 p-5 flex flex-col ${plan.popular ? 'border-accent-500 shadow-sm' : 'border-background-200'}`}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground-800">{plan.name}</h3>
                {plan.popular && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-accent-50 text-accent-600">Popular</span>
                )}
              </div>
              <div className="mt-2 flex items-end gap-2">
                <span className="text-2xl font-bold text-foreground-800">{plan.price}</span>
                <span className="text-sm text-foreground-400">{plan.period}</span>
                {discountPct(plan) > 0 && (
                  <span className="text-sm text-foreground-400 line-through mb-0.5">{inr(plan.mrp!)}</span>
                )}
              </div>
              <span className="text-xs text-foreground-400 mt-0.5">per outlet</span>
              {discountPct(plan) > 0 && (
                <span className="mt-1 self-start text-[11px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full">{discountPct(plan)}% OFF</span>
              )}
              <p className="text-xs text-foreground-500 mt-1">{plan.description}</p>
              <ul className="mt-4 space-y-1.5 flex-1">
                {plan.features.slice(0, 7).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-foreground-600">
                    <i className="ri-check-line text-primary-500 mt-0.5"></i>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => upgrade(plan.tier, plan.priceValue)}
                disabled={!configured || busy !== null}
                className={`mt-5 px-4 py-2 text-sm font-semibold rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${plan.popular ? 'bg-accent-500 text-background-50 hover:bg-accent-600' : 'bg-primary-500 text-background-50 hover:bg-primary-600'}`}
              >
                {busy === plan.tier ? 'Opening checkout…' : `Upgrade to ${plan.name}`}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
